import { useSelector } from 'react-redux';
import { OnboardingChrome } from '../components';
import {
  selectClient,
  selectFeatureFlags,
  selectInferenceSet,
  selectOnboardingSlideTracker,
} from '../model';

import { Button } from '@client/components/ui/button';
import { cn } from '@client/lib/utils';
import { formatPercent, formatThousands } from '@shared';
import { LtcCostDisplay } from '@client/components/LtcCostDisplay';
import {
  ContactLtcSpecialistButton,
  LtcFundingSlideOutContent,
  OnboardingDownloadReport,
  OnboardingFundingSources,
  SlideOutSheet,
  agisEnrollmentUrl,
  citations,
  onboardingSlideByIndex,
} from '..';
import { SlideContent } from '../components/SlideContent';
export function OnboardingFundingCoverage({ clientId }: ClientIdProps) {
  const client = useSelector(selectClient)!;
  const onboardingSlideTracker = useSelector(selectOnboardingSlideTracker);
  const featureFlags = useSelector(selectFeatureFlags);
  const vbShopFeatureFlag = featureFlags.vbShop;
  const agisFeatureFlag = featureFlags.agis;
  const groupPolicyFeatureFlag = vbShopFeatureFlag || agisFeatureFlag;

  const previousSlide =
    onboardingSlideTracker.previousSlideIndex === 0
      ? OnboardingFundingSources
      : onboardingSlideByIndex[onboardingSlideTracker.previousSlideIndex];
  const {
    fundingSources: {
      combinedProjectedCostCoverage,
      combinedProjectedCostCoveragePercent,
      selfFunding: { hasSelfFunding },
    },
  } = client;

  const beginningText = determineBeginningText(
    client.fundingSources,
    groupPolicyFeatureFlag ?? false,
  );
  const showNoCoverageSelectedText = !combinedProjectedCostCoverage && groupPolicyFeatureFlag;

  return (
    <OnboardingChrome
      slidePage={OnboardingFundingCoverage}
      nextSlidePage={OnboardingDownloadReport}
      previousSlidePage={previousSlide}
      nextButtonLabel="Next"
      citations={hasSelfFunding ? [citations.OnboardingFundingCoverageDisclaimer] : undefined}
    >
      <SlideContent
        headerText={''}
        data-testid="coverage-header"
      >
        {/* Hide for VB Shop if Zero coverage */}
        {!showNoCoverageSelectedText && (
          <>
            <FundingCoverageText
              beginningText={beginningText}
              combinedProjectedCostCoverage={combinedProjectedCostCoverage}
              combinedProjectedCostCoveragePercent={combinedProjectedCostCoveragePercent}
            />
            <HowDidWeCalculateThis />
          </>
        )}

        {showNoCoverageSelectedText && (
          <div className="flex flex-col items-center justify-center gap-4">
            <NoCoverageSelectedText className="mb-4 mt-8" />

            <ContactLtcSpecialistButton
              featureFlags={featureFlags}
              buttonClassName="w-full p-6 md:w-auto"
              iconClassName="h-6 w-6"
              children="Talk to an LTC Specialist"
            />
          </div>
        )}

        {groupPolicyFeatureFlag && !showNoCoverageSelectedText && (
          <div className="my-8 flex flex-col items-center justify-center gap-2 md:flex-row">
            <Button className="w-full p-6 md:w-auto">
              {featureFlags.agis ? (
                <a
                  href={agisEnrollmentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Enroll Now
                </a>
              ) : (
                'Enroll Now'
              )}
            </Button>

            <ContactLtcSpecialistButton
              featureFlags={featureFlags}
              buttonClassName="w-full p-6 md:w-auto"
              iconClassName="h-6 w-6"
              children="Talk to an LTC Specialist"
              variant="secondary"
            />
          </div>
        )}
      </SlideContent>
    </OnboardingChrome>
  );
}

type FundingCoverageTextProps = {
  beginningText: string;
  combinedProjectedCostCoverage: number | undefined;
  combinedProjectedCostCoveragePercent: number | undefined;
};

function HowDidWeCalculateThis() {
  return (
    <SlideOutSheet
      slideOutContent={<LtcFundingSlideOutContent />}
      title="FAQ"
    >
      <div
        className="hover:text-purple mt-8 text-center text-base text-gray-800 underline"
        data-testid="coverage-faq-link"
      >
        How did we calculate this?
      </div>
    </SlideOutSheet>
  );
}

function FundingCoverageText({
  beginningText,
  combinedProjectedCostCoverage,
  combinedProjectedCostCoveragePercent,
}: FundingCoverageTextProps) {
  return (
    <div className="mt-8 flex h-full w-full flex-col items-center justify-center text-center md:mt-16">
      <h1
        className="text-2xl font-semibold md:text-4xl"
        data-testid="coverage-text"
      >
        {beginningText} is projected to cover{' '}
        <span
          className="text-primary"
          data-testid="coverage-amount"
        >
          <LtcCostDisplay value={combinedProjectedCostCoverage ?? 0} />
        </span>
        , or{' '}
        <span
          className="text-primary"
          data-testid="coverage-percent"
        >
          {combinedProjectedCostCoverage
            ? formatPercent(combinedProjectedCostCoveragePercent, 0)
            : '0%'}{' '}
        </span>
        of your total predicted LTC costs! 🎉
      </h1>
    </div>
  );
}

function NoCoverageSelectedText({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'flex h-full w-full flex-col items-center justify-center gap-4 text-center md:gap-2',
        className,
      )}
    >
      <h1 className="flex items-center text-2xl font-semibold md:text-3xl">
        Did you mean to waive your long-term care benefits?
      </h1>
      <LtcProjections className="mt-4" />
      <p className="mt-4 text-lg md:text-xl">
        We can help you understand your options and potentially{' '}
        <span className="font-semibold">lower your future costs.</span>
      </p>
    </div>
  );
}

function determineBeginningText(fundingSources: FundingSources, groupPolicyFeatureFlag: boolean) {
  const {
    ltcPolicy: { hasPolicyFunding },
    selfFunding: { hasSelfFunding },
  } = fundingSources;

  const hasLtcPolicy = hasPolicyFunding === true;
  const hasOnlySelfFunding = hasSelfFunding && !hasLtcPolicy;
  const hasOnlyPolicyFunding = hasLtcPolicy && !hasSelfFunding;
  const hasBothFunding = hasSelfFunding && hasLtcPolicy;

  if (groupPolicyFeatureFlag) {
    return 'The option you selected';
  }

  if (hasOnlySelfFunding) {
    return 'Your self-funding plan';
  }

  if (hasOnlyPolicyFunding) {
    return 'Your policy';
  }

  if (hasBothFunding) {
    return 'Your policy and self-funding plan';
  }

  return 'Your current plan';
}

function LtcProjections({ className }: { className?: string }) {
  const { ltcLikelihoodEver } = useSelector(selectInferenceSet);
  const {
    appliedInferenceSet: { ltcAtAge },
  } = useSelector(selectClient);
  const {
    intakeSurvey: { clientHasStartedLtc },
    allPhaseCosts: { allPhaseInflatedProfessionalShareCost, allPhaseCareMonthsNeeded },
  } = useSelector(selectClient);

  // Base projections that are always shown
  const baseProjections = [
    {
      name: 'LTC Costs',
      isLtcCost: true as const,
      value: allPhaseInflatedProfessionalShareCost,
    },
    {
      name: clientHasStartedLtc ? 'Remaining Duration (Years)' : 'Duration (Years)',
      isLtcCost: false as const,
      value: formatThousands(allPhaseCareMonthsNeeded / 12, '0', 1),
    },
  ];

  // Additional projections for clients who haven't started LTC
  const additionalProjections = clientHasStartedLtc
    ? []
    : [
        {
          name: 'Likelihood of LTC',
          isLtcCost: false as const,
          value: formatPercent(ltcLikelihoodEver, 0),
        },
        {
          name: 'Start Age',
          isLtcCost: false as const,
          value: ltcAtAge,
          unit: 'years old',
        },
      ];

  const projections = [...baseProjections, ...additionalProjections];
  return (
    <div className={cn('mx-auto max-w-7xl px-6 lg:px-8', className)}>
      <div className="mx-auto max-w-2xl lg:max-w-none">
        <div className="mt-2">
          <h2 className="font-medium">Your Long-Term Care Projections</h2>
        </div>
        <dl
          className={`mt-4 grid grid-cols-1 gap-0.5 overflow-hidden rounded-2xl text-center sm:grid-cols-2 lg:grid-cols-${projections.length}`}
        >
          {projections.map(stat => (
            <div
              key={stat.name}
              className="flex flex-col bg-gray-400/5 p-8"
            >
              <dt className="text-sm font-semibold leading-6 text-gray-700">{stat.name}</dt>
              <dd className="order-first text-3xl font-semibold tracking-tight text-gray-900">
                {stat.isLtcCost ? (
                  <LtcCostDisplay value={stat.value as number} />
                ) : (
                  stat.value
                )}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}
